"use client";

import {
  DocumentData,
  FirestoreDataConverter,
  Firestore,
  Query,
  QueryConstraint,
  collection,
  getDocs,
  onSnapshot,
  query,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";

type useFetchCollectionProp<T> = {
  db: Firestore;
  path: string;
  converter: FirestoreDataConverter<T>;
  query?: QueryConstraint | QueryConstraint[];
  realtime?: boolean;
  skip?: boolean;
};

export const useFetchCollection = <T>({
  db,
  path,
  converter,
  query: collectionQuery,
  realtime = false,
  skip = false,
}: useFetchCollectionProp<T>) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T[]>([]);

  const fetchedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isSettingUpRef = useRef(false);
  const lastQueryValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (skip) {
      // Keep loading state when skipped (e.g., waiting for orgUid)
      setLoading(true);
      setData([]);
      setError(null);
      return;
    }

    if (!realtime && fetchedRef.current) {
      return;
    }

    // Create query ref - do this before any async operations
    let ref: Query<T, DocumentData> = collection(db, path).withConverter(
      converter,
    );
    if (collectionQuery) {
      ref = Array.isArray(collectionQuery)
        ? query(ref, ...collectionQuery)
        : query(ref, collectionQuery);
    }

    if (realtime) {
      // Extract query value for comparison (if it's a where clause, get the value)
      // This is a simple heuristic - for where("field", "==", value), we track the value
      let queryValue: string | null = null;
      if (collectionQuery) {
        // Try to extract the value from the constraint
        // This is a workaround since we can't easily serialize QueryFieldFilterConstraint
        // We'll use a combination of path and a simple identifier
        queryValue = `${path}:has-query`;
      } else {
        queryValue = `${path}:no-query`;
      }

      // Only clean up existing listener if the query actually changed
      const existingUnsubscribe = unsubscribeRef.current;
      const queryChanged = lastQueryValueRef.current !== queryValue;
      
      if (existingUnsubscribe && queryChanged) {
        // Clean up existing listener first
        existingUnsubscribe();
        unsubscribeRef.current = null;
      } else if (existingUnsubscribe && !queryChanged) {
        // Query hasn't changed, keep existing listener
        return;
      }
      
      // Update the last query ref
      lastQueryValueRef.current = queryValue;

      // Check if setup is already in progress
      if (isSettingUpRef.current) {
        return;
      }

      // Mark as setting up BEFORE calling onSnapshot
      isSettingUpRef.current = true;

      // Defer setup to next microtask to allow React Strict Mode cleanup to complete
      // This prevents race conditions when Strict Mode calls cleanup synchronously
      const setupTimeout = setTimeout(() => {
        // Double-check we're still supposed to set up (cleanup might have run)
        if (unsubscribeRef.current) {
          // Listener already exists, skip (shouldn't happen, but safety check)
          isSettingUpRef.current = false;
          return;
        }
        
        if (!isSettingUpRef.current) {
          // Setup was cancelled (cleanup ran), skip
          return;
        }

        // Let React's useEffect cleanup handle listener teardown naturally
        // This avoids race conditions from manual cleanup/setup cycles
        const unsubscribe = onSnapshot(
          ref,
          (snapshot) => {
            const collectionList = snapshot.docs.map((doc) => doc.data());
            setData(collectionList);
            setLoading(false);
          },
          (error) => {
            setError(`Failed to load collection. ${error}`);
            setLoading(false);
          },
        );
        
        // Store unsubscribe and mark setup as complete
        unsubscribeRef.current = unsubscribe;
        isSettingUpRef.current = false;
      }, 0);

      return () => {
        clearTimeout(setupTimeout);
        // Reset flags to prevent deferred setup from proceeding
        isSettingUpRef.current = false;
        // Reset refs before unsubscribing to allow next setup
        const fn = unsubscribeRef.current;
        unsubscribeRef.current = null;
        if (fn) {
          fn();
        }
      };
    } else {
      // Non-realtime: fetch once
      const fetchData = async () => {
        try {
          const querySnapshot = await getDocs(ref);
          const collectionList = querySnapshot.docs.map((doc) => doc.data());
          setData(collectionList);
          fetchedRef.current = true; // Mark as fetched
        } catch (error) {
          setError(`Failed to load collection. ${error}`);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
      return () => {};
    }
  }, [db, path, realtime, collectionQuery, converter, skip]);

  return { data, loading, error };
};

